import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { register, login, getActiveProfile } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { landingHref, go } from "../lib/hosts";

const INACTIVE_MSG = "Akun Anda belum aktif. Silakan hubungi admin Landhep untuk mengaktifkan akun Anda.";
// New accounts wait for an admin to activate them, so "contact the admin" has to be
// something the visitor can actually do — the same WhatsApp line as the landing page.
const ADMIN_WA = "6285111214177";
function adminWaUrl(email) {
  const text = email
    ? `Halo tim Landhep, saya sudah mendaftar dan ingin mengaktifkan akun saya: ${email}`
    : "Halo tim Landhep, saya sudah mendaftar dan ingin mengaktifkan akun saya.";
  return `https://wa.me/${ADMIN_WA}?text=${encodeURIComponent(text)}`;
}

export default function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const [tab, setTab] = useState("login"); // "login" | "signup"
  const [showPass, setShowPass] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [loading, setLoading] = useState(false);

  // Handle ?error=account_inactive from Google OAuth redirect
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("error") === "account_inactive") {
      setError(INACTIVE_MSG);
    }
  }, [location.search]);

  async function handleSubmit() {
    setError("");
    setSuccessMsg("");
    if (!email.trim() || !password) { setError("Email dan kata sandi wajib diisi."); return; }
    if (tab === "signup" && password.length < 8) { setError("Kata sandi minimal 8 karakter."); return; }
    setLoading(true);
    try {
      if (tab === "signup") {
        await register({ email, password, full_name: fullName });
        setSuccessMsg("Pendaftaran berhasil! Akun Anda menunggu aktivasi. Silakan hubungi admin Landhep untuk mengaktifkan akun Anda.");
        setEmail(""); setPassword(""); setFullName("");
        return;
      }
      const user = await login({ email, password });
      setUser(user);
      // Route by whether the user already has a business profile.
      const profile = await getActiveProfile();
      navigate(profile ? "/analysis" : "/onboarding");
    } catch (e) {
      if (e.status === 403 || (e.message || "").toLowerCase().includes("not yet active")) {
        setError(INACTIVE_MSG);
      } else {
        setError(e.message || "Terjadi kesalahan.");
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const els = [document.documentElement, document.body, document.getElementById("root")];
    els.forEach((el) => { if (el) { el.style.overflow = "auto"; el.style.height = "auto"; } });
    return () => {
      els.forEach((el) => { if (el) { el.style.overflow = ""; el.style.height = ""; } });
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#f1f5f3] text-[#171C1F] antialiased">
      <header className="sticky top-0 z-40 w-full border-b border-slate-100 bg-white/90 shadow-sm backdrop-blur-md">
        <div className="mx-auto flex h-16 w-full max-w-[1280px] items-center gap-3 px-6 md:px-12">
          <button
            onClick={() => go(navigate, landingHref("/"))}
            className="rounded-lg p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
            aria-label="Kembali ke halaman utama"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <span className="text-xl font-extrabold tracking-tight text-brand-700">Landhep</span>
        </div>
      </header>

      <div className="relative flex min-h-[calc(100vh-64px)] items-center overflow-hidden bg-[#0c3124] px-6 py-16">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_45%,rgba(29,145,96,0.25),transparent_40%),radial-gradient(circle_at_78%_60%,rgba(18,128,90,0.22),transparent_45%),linear-gradient(135deg,#08221a_0%,#104230_55%,#0c3124_100%)]" />
        <div className="absolute inset-0 opacity-30 mix-blend-screen">
          <img
            className="h-full w-full object-cover"
            src="https://lh3.googleusercontent.com/aida-public/AB6AXuAi2NbHIi9N6WW4WiZk8jo59GpJJ05okwa2XZIq9om2SRFf-P8Am4FHkUMbdXaDf458GvZ3hYrf_pDD_jrWi3oCC1d_veQB-eM1l7Y2AWPVnwGv7SC5mfP6Byrl7wlaH_ojzMo6I2AdGHxVVinXqi2-TSkYX7xIZ5JPPzVSwL-3rCPBN5nIaGe0zjtQlpUjg889cSxTw39yIPm7wf8E1Mc9eyzLvNHQ-htLmfCH3bYDF0peNMBkp4Dm3_N8rg8-c6RPIz3fkSak2Qg"
            alt="Latar pola peta"
          />
        </div>
        <div className="absolute inset-0 bg-[linear-gradient(rgba(92,176,141,0.14)_1px,transparent_1px),linear-gradient(90deg,rgba(92,176,141,0.14)_1px,transparent_1px)] bg-[size:36px_36px]" />
        <div className="absolute inset-0 bg-[radial-gradient(120%_70%_at_80%_85%,rgba(29,145,96,0.24),transparent_55%),radial-gradient(80%_50%_at_70%_70%,rgba(18,128,90,0.22),transparent_58%),radial-gradient(70%_40%_at_60%_78%,rgba(92,176,141,0.18),transparent_60%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_top,rgba(1,10,35,0.75)_0%,rgba(1,10,35,0.2)_40%,rgba(1,10,35,0.45)_100%)]" />

        <div className="relative mx-auto grid w-full max-w-[1280px] items-center gap-10 lg:grid-cols-2">
          <div className="hidden space-y-6 text-white lg:block">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#99ceb7]">Akses Aman</p>
            <h1 className="text-4xl font-extrabold leading-tight">
              Masuk ke Landhep dan mulai analisis lokasi dengan presisi.
            </h1>
            <p className="max-w-xl text-lg leading-relaxed text-slate-300">
              Gunakan akun Anda untuk membuka wawasan lokasi, membandingkan area potensial, dan mengambil keputusan ekspansi berbasis data.
            </p>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm text-white/90 backdrop-blur-md">
              <ShieldCheck className="h-4 w-4 text-[#99ceb7]" />
              Akses terenkripsi dan perlindungan data
            </div>
          </div>

          <div className="w-full max-w-md justify-self-center">
            <div className="rounded-2xl border border-white/15 bg-white/95 p-8 shadow-2xl backdrop-blur-xl">
              <div className="mb-8 flex gap-1 rounded-xl bg-slate-100 p-1">
            {["login", "signup"].map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`flex-1 rounded-lg py-2 text-sm font-semibold transition-all ${
                  tab === t
                    ? "bg-white text-[#171C1F] shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {t === "login" ? "Masuk" : "Daftar"}
              </button>
            ))}
          </div>

          <h1 className="mb-1 text-2xl font-bold text-[#171C1F]">
            {tab === "login" ? "Selamat datang kembali" : "Buat akun Anda"}
          </h1>
          <p className="mb-7 text-sm text-slate-500">
            {tab === "login"
              ? "Masuk untuk mengakses dasbor analisis Anda."
              : "Mulai menemukan lokasi yang tepat untuk bisnis Anda."}
          </p>

          <div className="mb-4 flex flex-col gap-4">
            {tab === "signup" && (
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-[#3E4947]">Nama Lengkap</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Nama Anda"
                  className="w-full rounded-xl border border-[#BDC9C6] bg-slate-50 px-4 py-2.5 text-sm placeholder-slate-400 outline-none transition-colors focus:border-[#14543e] focus:ring-1 focus:ring-[#14543e]"
                />
              </div>
            )}

            <div>
              <label className="mb-1.5 block text-xs font-semibold text-[#3E4947]">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="anda@email.com"
                className="w-full rounded-xl border border-[#BDC9C6] bg-slate-50 px-4 py-2.5 text-sm placeholder-slate-400 outline-none transition-colors focus:border-[#14543e] focus:ring-1 focus:ring-[#14543e]"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold text-[#3E4947]">Kata Sandi</label>
              <div className="relative">
                <input
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
                  placeholder="••••••••"
                  className="w-full rounded-xl border border-[#BDC9C6] bg-slate-50 px-4 py-2.5 pr-10 text-sm placeholder-slate-400 outline-none transition-colors focus:border-[#14543e] focus:ring-1 focus:ring-[#14543e]"
                />
                <button
                  type="button"
                  onClick={() => setShowPass((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-600"
                  aria-label={showPass ? "Sembunyikan kata sandi" : "Tampilkan kata sandi"}
                >
                  {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>

          {tab === "login" && (
            <div className="mb-6 text-right">
              <button className="text-xs text-[#14543e] hover:underline">Lupa kata sandi?</button>
            </div>
          )}
          {tab === "signup" && <div className="mb-6" />}

          {/* Waiting for activation is not a failure, so it is not shown in red. The
              email is only known on the password path; the Google path arrives with
              nothing but ?error=account_inactive, and an address in the URL would land
              in logs and history. */}
          {error === INACTIVE_MSG ? (
            <div className="mb-4 rounded-xl border border-brand-200 bg-brand-50 px-4 py-2.5 text-xs font-medium text-brand-800">
              Akun Anda belum aktif. Silakan{" "}
              <a href={adminWaUrl(email.trim())} target="_blank" rel="noopener noreferrer"
                className="font-bold text-brand-700 underline underline-offset-2 hover:text-brand-600">
                hubungi admin Landhep
              </a>{" "}
              untuk mengaktifkan akun Anda.
            </div>
          ) : error && (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-xs font-medium text-red-600">
              {error}
            </div>
          )}
          {successMsg && (
            <div className="mb-4 rounded-xl border border-green-200 bg-green-50 px-4 py-2.5 text-xs font-medium text-green-700">
              {successMsg}
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={loading || !!successMsg}
            className="mb-4 w-full rounded-full bg-[#14543e] py-3 text-sm font-semibold text-white transition-all hover:bg-brand-700 disabled:opacity-50"
          >
            {loading ? "Mohon tunggu…" : tab === "login" ? "Masuk" : "Daftar"}
          </button>

          <div className="mb-4 flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-xs text-slate-400">atau</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <button
            onClick={() => { window.location.href = "/api/auth/google"; }}
            className="flex w-full items-center justify-center gap-2.5 rounded-full border border-[#BDC9C6] py-2.5 text-sm font-medium text-[#3E4947] transition-colors hover:bg-slate-50"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24">
              <path fill="#12805a" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Lanjutkan dengan Google
          </button>

          <p className="mt-6 text-center text-xs text-slate-400">
            {tab === "login" ? "Belum punya akun? " : "Sudah punya akun? "}
            <button
              onClick={() => setTab(tab === "login" ? "signup" : "login")}
              className="font-medium text-[#14543e] hover:underline"
            >
              {tab === "login" ? "Daftar" : "Masuk"}
            </button>
          </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
